import type { MouseEvent as ReactMouseEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

interface DragAnchor {
    x: number;
    y: number;
}

interface UseDraggablePopoverOptions {
    anchor: DragAnchor | null;
    width: number;
    height: number;
    offsetY?: number;
    margin?: number;
}

function clampPosition(x: number, y: number, width: number, height: number, margin: number) {
    const maxX = Math.max(margin, window.innerWidth - width - margin);
    const maxY = Math.max(margin, window.innerHeight - height - margin);

    return {
        x: Math.min(Math.max(x, margin), maxX),
        y: Math.min(Math.max(y, margin), maxY),
    };
}

export function useDraggablePopover({
    anchor,
    width,
    height,
    offsetY = 4,
    margin = 8,
}: UseDraggablePopoverOptions) {
    const anchorX = anchor?.x ?? null;
    const anchorY = anchor?.y ?? null;
    const [position, setPosition] = useState<DragAnchor | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const startPointerRef = useRef<DragAnchor | null>(null);
    const startPositionRef = useRef<DragAnchor | null>(null);
    const sizeRef = useRef({ width, height });
    sizeRef.current = { width, height };
    const marginRef = useRef(margin);
    marginRef.current = margin;

    const stopDragging = useCallback(() => {
        startPointerRef.current = null;
        startPositionRef.current = null;
        setIsDragging(false);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
    }, []);

    useEffect(() => {
        if (anchorX == null || anchorY == null) {
            setPosition(null);
            stopDragging();
            return;
        }

        setPosition(
            clampPosition(
                anchorX,
                anchorY + offsetY,
                sizeRef.current.width,
                sizeRef.current.height,
                margin,
            ),
        );
    }, [anchorX, anchorY, offsetY, margin, stopDragging]);

    useEffect(() => {
        setPosition((current) =>
            current ? clampPosition(current.x, current.y, width, height, margin) : current,
        );
    }, [width, height, margin]);

    const beginDragging = useCallback(
        (eventTarget: EventTarget | null, clientX: number, clientY: number, preventDefault?: () => void) => {
            const target = eventTarget as HTMLElement | null;
            if (!target) return;
            if (
                target.closest(
                    "button, input, textarea, select, a, [role='button'], [data-no-drag='true']",
                )
            ) {
                return;
            }

            const currentPosition = position ?? (anchor
                ? clampPosition(anchor.x, anchor.y + offsetY, width, height, margin)
                : null);
            if (!currentPosition) return;

            preventDefault?.();
            startPointerRef.current = { x: clientX, y: clientY };
            startPositionRef.current = currentPosition;
            setIsDragging(true);
            document.body.style.cursor = "grabbing";
            document.body.style.userSelect = "none";
        },
        [anchor, height, margin, offsetY, position, width],
    );

    const onHeaderMouseDown = useCallback(
        (e: ReactMouseEvent<HTMLElement>) => {
            if (e.button !== 0) return;
            beginDragging(e.target, e.clientX, e.clientY, () => e.preventDefault());
        },
        [beginDragging],
    );

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            const startPointer = startPointerRef.current;
            const startPosition = startPositionRef.current;
            if (!startPointer || !startPosition) return;

            const deltaX = e.clientX - startPointer.x;
            const deltaY = e.clientY - startPointer.y;
            setPosition(
                clampPosition(
                    startPosition.x + deltaX,
                    startPosition.y + deltaY,
                    sizeRef.current.width,
                    sizeRef.current.height,
                    marginRef.current,
                ),
            );
        };

        const handleMouseUp = () => {
            if (!startPointerRef.current) return;
            stopDragging();
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            stopDragging();
        };
    }, [stopDragging]);

    return {
        isDragging,
        onHeaderMouseDown,
        position,
    };
}
